// src/encounter/wrap-up-pending-orders-dialogs.tsx
// The two confirmations behind the wrap-up Pending orders list (PROD-2594), on the DS Modal the
// rest of the wrap-up invoice uses (management discount, add service). Copy is pm-react's
// (`invoice-pending-orders-dialogs.tsx`, PROD-2591) word for word; only the chrome differs —
// pm-react uses a shadcn AlertDialog and a Popover, practitioner-react composes Signal DS.
import { useState } from 'react';
import { Button, Modal, RadioGroup, TextArea } from '@scripsteam/scrips-signal-ds';
import { formatMoney } from '@/shared/lib/money';
import type { InvoiceOrderDismissReason } from '@/shared/types/billing';
import { DISMISS_REASONS, DISMISS_REASON_LABEL, DISMISS_TOOLTIP, joinNames } from '@/billing/lib/pending-orders';

export interface AddOrdersConfirmTarget {
  /** Order names, one per order (a vaccine's two lines are one order). */
  names: string[];
  /** Sum of the indicative fees being added. */
  amount: number;
}

interface AddOrdersConfirmModalProps {
  target: AddOrdersConfirmTarget | null;
  currency: string;
  /** Patient Payable before the add; with `selfPay` shows the "a -> b" line. */
  patientPayable?: number;
  selfPay: boolean;
  userName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** "Add ECG, 12-lead to the invoice now?" — the add moves money, so it is confirmed. */
export function AddOrdersConfirmModal({
  target,
  currency,
  patientPayable,
  selfPay,
  userName,
  pending,
  onCancel,
  onConfirm,
}: AddOrdersConfirmModalProps) {
  const count = target?.names.length ?? 0;
  const single = count === 1;
  const amount = target?.amount ?? 0;
  const money = (n: number) => `${currency} ${formatMoney(n)}`;
  const title = single ? `Add ${target?.names[0]} to the invoice now?` : `Add ${count} orders to the invoice now?`;
  const description = single
    ? 'The order stays as it is in Assessment & Plan. Only the invoice changes.'
    : `${joinNames(target?.names ?? [])}. The orders stay as they are in Assessment & Plan.`;

  const footer = (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
      {/* Stays open until the request settles; the parent closes it. */}
      <Button onClick={onConfirm} disabled={pending} loading={pending}>
        {single ? 'Add to invoice' : `Add ${count} to invoice`}
      </Button>
    </>
  );

  return (
    <Modal
      open={target !== null}
      onClose={() => !pending && onCancel()}
      title={title}
      description={description}
      footer={footer}
      size="sm"
    >
      <ul className="m-0 flex list-none flex-col gap-2 p-0 text-size-sm text-text-secondary">
        <li className="flex gap-2">
          <span className="text-text-muted" aria-hidden>
            •
          </span>
          {selfPay && patientPayable !== undefined ? (
            <span>
              <strong className="font-semibold text-text-primary">{money(amount)}</strong> moves into Patient
              Payable ({money(patientPayable)} → {money(patientPayable + amount)}).
            </span>
          ) : (
            <span>
              <strong className="font-semibold text-text-primary">{money(amount)}</strong> is added to the invoice.
              Coverage decides how much of it the patient pays.
            </span>
          )}
        </li>
        <li className="flex gap-2">
          <span className="text-text-muted" aria-hidden>
            •
          </span>
          <span>
            {single ? 'The line is' : 'Each line is'} tagged{' '}
            <em className="font-medium not-italic text-brand-depth">Added by {userName}</em> with the time.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-text-muted" aria-hidden>
            •
          </span>
          <span>
            {single
              ? 'If the order is cancelled later: before issue you can revert the line to pending or drop it; after issue it needs a credit or refund.'
              : 'If an order is cancelled later: before issue you can revert or drop its line; after issue it needs a credit or refund.'}
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-text-muted" aria-hidden>
            •
          </span>
          <span>{single ? 'Dropping the line later never cancels the order.' : 'Dropping a line later never cancels its order.'}</span>
        </li>
      </ul>
    </Modal>
  );
}

export interface DismissChoice {
  reason?: InvoiceOrderDismissReason;
  note?: string;
}

interface DismissOrdersModalProps {
  /** "Dismiss HbA1c from this invoice?" / "Dismiss 2 orders from this invoice?". Null → closed. */
  title: string | null;
  pending: boolean;
  onCancel: () => void;
  /** Resolve true to close (the request succeeded). */
  onConfirm: (choice: DismissChoice) => Promise<boolean>;
}

const NOTE_MAX = 500;

const REASON_OPTIONS = DISMISS_REASONS.map((r) => ({ label: DISMISS_REASON_LABEL[r], value: r }));

/**
 * Dismiss with an optional reason. "Other" reveals a short note (≤ 500). Dismissing never
 * cancels the clinical order, which the copy says. The host remounts it per target (`key`), so
 * the reason and note always start empty.
 */
export function DismissOrdersModal({ title, pending, onCancel, onConfirm }: DismissOrdersModalProps) {
  const [reason, setReason] = useState<InvoiceOrderDismissReason | undefined>(undefined);
  const [note, setNote] = useState('');

  const submit = async () => {
    const trimmed = note.trim();
    const ok = await onConfirm({ reason, note: reason === 'Other' && trimmed ? trimmed : undefined });
    if (ok) onCancel();
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
      <Button onClick={submit} disabled={pending} loading={pending}>
        Dismiss
      </Button>
    </>
  );

  return (
    <Modal
      open={title !== null}
      onClose={() => !pending && onCancel()}
      title={title ?? ''}
      description={DISMISS_TOOLTIP}
      footer={footer}
      size="sm"
    >
      <div className="flex flex-col gap-3">
        <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
          <legend className="mb-1 text-size-2xs font-bold uppercase tracking-eyebrow text-text-muted">
            Reason (optional)
          </legend>
          <RadioGroup<InvoiceOrderDismissReason>
            name="dismiss-reason"
            options={REASON_OPTIONS}
            value={reason}
            onChange={setReason}
            orientation="vertical"
          />
        </fieldset>
        {reason === 'Other' && (
          <div className="flex flex-col gap-1">
            <TextArea
              aria-label="Note"
              placeholder="Add a short note (optional)"
              maxLength={NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
            <span className="self-end text-size-2xs tabular-nums text-text-muted">
              {note.length}/{NOTE_MAX}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
